import styled from 'styled-components';

import { CopyValue } from 'components/CopyValue';
import { StatusTag } from './StatusTag';

import { getExtendedFormattedDatetime } from 'helpers/dateTimes';
import { GetUserQuery } from 'data/__generated__';

type Props = {
    user: NonNullable<GetUserQuery['userFromAdmin']>;
};

const buildInitials = (firstname: string | null | undefined, lastname: string | null | undefined) => {
    const first = firstname?.trim()[0] ?? '';
    const last = lastname?.trim()[0] ?? '';
    return `${first}${last}`.toUpperCase() || '?';
};

export const IdentityBanner = ({ user }: Props) => {
    const {
        _id: userId,
        createdAt,
        lastSeenAt,
        organization,
        profile: { firstname, lastname, fullname, imageUrl, state },
        stripeCustomerId,
        username,
    } = user;

    const externalLinks: { label: string; href: string }[] = [
        {
            label: '💌 Postmark',
            href: `https://account.postmarkapp.com/servers/5335708/streams/outbound/addresses/${username}`,
        },
        {
            label: '💰 Stripe',
            href: `https://dashboard.stripe.com/customers${
                stripeCustomerId ? `/${stripeCustomerId}` : `?email=${username}`
            }`,
        },
        {
            label: '🧐 Mixpanel',
            href: `https://eu.mixpanel.com/project/2786792/view/3321576/app/profile#distinct_id=${userId}`,
        },
        {
            label: '🛎 Customer.io',
            href: `https://fly.customer.io/env/108000/people?email=${username}`,
        },
        {
            label: '🐞 Sentry',
            href: `https://totem.sentry.io/issues/?environment=prod&?project=2204925&query=user.id%3A${userId}&referrer=issue-list&statsPeriod=360d`,
        },
    ];

    return (
        <Container>
            <Left>
                <Meta>
                    <MetaLabel>ID</MetaLabel>
                    <CopyValue value={userId} />
                </Meta>
                <Meta>
                    <MetaLabel>Inscription</MetaLabel>
                    <MetaValue>{createdAt ? getExtendedFormattedDatetime(Number(createdAt)) : '—'}</MetaValue>
                </Meta>
                <Meta>
                    <MetaLabel>Dernière connexion</MetaLabel>
                    <MetaValue>{lastSeenAt ? getExtendedFormattedDatetime(lastSeenAt) : 'jamais'}</MetaValue>
                </Meta>
                <Links>
                    {externalLinks.map(({ label, href }) => (
                        <ExternalChip key={label} href={href} target="_blank" rel="noopener noreferrer">
                            {label}
                        </ExternalChip>
                    ))}
                </Links>
            </Left>
            <Right>
                {imageUrl ? (
                    <AvatarImage src={imageUrl} alt={fullname ?? username} />
                ) : (
                    <Avatar>{buildInitials(firstname, lastname)}</Avatar>
                )}
                <Identity>
                    <NameRow>
                        <Fullname>{fullname || `${firstname ?? ''} ${lastname ?? ''}`.trim() || 'Sans nom'}</Fullname>
                        <StatusTag state={state} />
                    </NameRow>
                    <Username>@{username}</Username>
                    {organization?.name ? <Organization>🏢 {organization.name}</Organization> : null}
                </Identity>
            </Right>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    gap: 24px;

    @media (max-width: 1100px) {
        flex-direction: column;
    }
`;

const Left = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 280px;
`;

const Right = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 16px;
    min-width: 0;
`;

const Avatar = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.ctaPrimaryColor};
    color: ${({ theme }) => theme.menuTextColor};
    font-size: 22px;
    font-weight: 600;
    flex-shrink: 0;
`;

const AvatarImage = styled.img`
    width: 64px;
    height: 64px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
    background-color: ${({ theme }) => theme.lightBorderColor};
`;

const Identity = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
`;

const NameRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
`;

const Fullname = styled.h2`
    margin: 0;
    font-size: 22px;
    color: ${({ theme }) => theme.textColor};
`;

const Username = styled.span`
    font-size: 14px;
    color: ${({ theme }) => theme.infoTextColor || theme.textColor};
`;

const Organization = styled.span`
    font-size: 13px;
    color: ${({ theme }) => theme.textColor};
    opacity: 0.85;
`;

const Meta = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
`;

const MetaLabel = styled.span`
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${({ theme }) => theme.textColor};
    opacity: 0.6;
    min-width: 130px;
`;

const MetaValue = styled.span`
    font-size: 13px;
    color: ${({ theme }) => theme.textColor};
`;

const Links = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 4px;
`;

const ExternalChip = styled.a`
    padding: 4px 10px;
    border-radius: 999px;
    background-color: ${({ theme }) => theme.backgroundColor};
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    color: ${({ theme }) => theme.ctaPrimaryColor};
    text-decoration: none;
    font-size: 12px;

    &:hover {
        text-decoration: underline;
    }
`;
